interface AttachmentsWidgetOptions {
  inputSelector: string;
  listSelector: string;
  allowedTypes?: string[]; // например ['image/png','application/pdf']
  maxFileSizeMB?: number; // например 10
}

export class AttachmentsListWidget {
  private inputSelector: string;
  private listSelector: string;
  private allowedTypes: string[];
  private maxFileSizeMB: number;

  private fileInput: HTMLInputElement | null = null;
  private fileListElement: HTMLElement | null = null;

  private selectedFiles: File[] = [];

  constructor(options: AttachmentsWidgetOptions) {
    this.inputSelector = options.inputSelector;
    this.listSelector = options.listSelector;
    this.allowedTypes = options.allowedTypes || [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    this.maxFileSizeMB = options.maxFileSizeMB || 10;
  }

  /**
   * Публичный метод инициализации
   */
  public init() {
    this.fileInput = document.querySelector<HTMLInputElement>(this.inputSelector);
    this.fileListElement = document.querySelector<HTMLElement>(this.listSelector);

    if (!this.fileInput || !this.fileListElement) {
      return;
    }

    this.fileInput.addEventListener('change', (e) => {
      const input = e.target as HTMLInputElement;
      if (!input.files) return;

      const newFiles = Array.from(input.files).filter((file) => this.validateFile(file));

      this.selectedFiles = this.selectedFiles.concat(newFiles);

      this.renderList();
      this.syncInputFiles();
    });
  }

  public async addFilesFromUrls(files: { url: string; name: string }[]): Promise<void> {
    this.selectedFiles = [];
    const requests = files.map((file) => this.loadFileFromUrl(file));

    const results = await Promise.allSettled(requests);

    const validFiles = results
      .filter((r): r is PromiseFulfilledResult<File> => r.status === 'fulfilled')
      .map((r) => r.value)
      .filter((file) => this.validateFile(file));

    this.selectedFiles = this.selectedFiles.concat(validFiles);
    this.renderList();
    this.syncInputFiles();
  }

  /**
   * Валидация файла
   */
  private validateFile(file: File): boolean {
    if (!this.allowedTypes.includes(file.type)) {
      alert(`Файл "${file.name}" имеет недопустимый формат.`);
      return false;
    }

    if (file.size > this.maxFileSizeMB * 1024 * 1024) {
      alert(`Файл "${file.name}" превышает максимальный размер ${this.maxFileSizeMB} МБ.`);
      return false;
    }

    return true;
  }

  /**
   * Перерисовка списка
   */
  private renderList() {
    if (!this.fileListElement) return;

    this.fileListElement.innerHTML = '';

    this.selectedFiles.forEach((file, index) => {
      const li = document.createElement('li');
      li.className = 'list-group-item d-flex align-items-center gap-2';

      li.innerHTML = `
        <span>
          <i class="bi bi-file-earmark me-2"></i>
          ${file.name}
        </span>
        <button type="button" class="btn btn-sm btn-outline-secondary" data-index="${index}">
          <i class="bi bi-x"></i>
        </button>
      `;

      this.fileListElement?.appendChild(li);
    });

    // обработка кнопок удаления
    this.fileListElement.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const index = Number(btn.getAttribute('data-index'));
        this.selectedFiles.splice(index, 1);
        this.renderList();
        this.syncInputFiles();
      });
    });
  }

  private async loadFileFromUrl({ url, name }: { url: string; name: string }): Promise<File> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Ошибка загрузки: ${url}`);
    }

    const blob = await response.blob();

    return new File([blob], name, { type: blob.type });
  }

  /**
   * Перезапись FileList в input
   */
  private syncInputFiles() {
    if (!this.fileInput) return;

    const dt = new DataTransfer();
    this.selectedFiles.forEach((file) => dt.items.add(file));

    this.fileInput.files = dt.files;
  }
}
